import {
  FacilityType,
  type BookingData,
  type BookingTypeConfigField,
  type HittingBaySlot,
} from '@/types/booking.types';
import type { LocationData } from '@/types/location.types';
import { formatDateForAPIWithTimezone } from '@/utils/date-utils';
import waiverService, { type WaiverData } from '@/services/waiverService';

/* ============================================================
   BOOKING RULES — shared between the v1 and v2 booking flows.

   WHY THIS FILE EXISTS

   Three pieces of logic decide whether a member is charged, what
   is actually sent to the API, and whether they are allowed to
   book at all. They were inline in `pages/Booking/SelectSlots.tsx`.
   The v2 slot picker needs the same three, and a second copy that
   drifts from this one does not fail loudly — it fails as members
   booked without being charged, or waivers not enforced.

   So they live here, as pure functions with no React and no
   component state, and both trees call them. v1 keeps all of its
   own state exactly where it was; only these three calls moved.

   Anything that changes what a member pays, or what the API is
   told, belongs in this file. Presentation does not.

   Both trees consume the payment gate below: `SelectSlots.tsx` (v1,
   one call site) and `v2/screens/SelectSlots.tsx` (v2, two call
   sites, ~lines 1256 and 1310). Changing the rule means checking
   all three.
   ============================================================ */

/** The slice of membership usage the payment gate reads. */
export type MembershipUsageLike = {
  totalFreeBookingCount?: number;
  freeBookingsAvailed?: number;
} | null;

/* ------------------------------------------------------------
   1 · PAYMENT GATE
   ------------------------------------------------------------ */

export type PaymentGateInput = {
  /**
   * Set by entitlement-derived booking types from
   * `BookingTypeDto.viaEntitlement`. Wins over every other input
   * below — an entitlement booking is never paid for at checkout.
   */
  viaEntitlement?: boolean;
  /** The club being booked. */
  selectedLocationId?: string;
  /** The member's own club. */
  userLocationId?: string;
  membershipUsage: MembershipUsageLike;
  /** Price of the booking type. Zero means a free drop-in. */
  bookingTypePrice?: number;
  /**
   * `BookingType.fields` as configured in Box OS. Read for
   * `max_concurrent_bookings` — see `isPayPerBooking` below.
   */
  bookingTypeFields?: Record<string, BookingTypeConfigField>;
};

/**
 * "Max Reservations per member in advance" = 0 means the plan includes
 * no reservations at all, so the member pays per booking. It is not a
 * cap of zero bookings.
 *
 * Mirrors `BookingTypeDto.isPayPerBooking()` on the server, which is what
 * marks the booking DROP_IN_PAID and refuses a create that arrives without
 * a payment — the two answers have to agree.
 */
export const isPayPerBooking = (
  fields?: Record<string, BookingTypeConfigField>,
): boolean => {
  const field = fields?.max_concurrent_bookings;
  return !!field?.enabled && Number(field.value) === 0;
};

/**
 * Whether this booking has to be paid for before it can be made.
 *
 * A member books free at their own club. Away from it they draw
 * on their free-booking allowance, and once that is spent — and
 * the booking type is not itself free — payment is required.
 *
 * Extracted verbatim from `handleCheckoutConfirm`; the ?? 0
 * defaults matter, because usage is undefined until it loads and
 * treating that as "no free bookings" would wrongly charge.
 */
export const isPaymentRequired = ({
  viaEntitlement,
  selectedLocationId,
  userLocationId,
  membershipUsage,
  bookingTypePrice,
  bookingTypeFields,
}: PaymentGateInput): boolean => {
  /* D-10: an entitlement booking is paid for by the ledger, so it
     never requires payment — regardless of the booking type's
     price, the member's location, or their free-booking allowance.
     The real price stays on the record untouched for admin/reporting
     (Phase 336 `entitlementsBasePrice`) and is deliberately not
     zeroed at the source. */
  if (viaEntitlement) return false;

  /* A plan with no included reservations charges for every booking, at
     the member's own club as much as anywhere else — so this answer is
     reached before the home/away split below. */
  if (isPayPerBooking(bookingTypeFields)) return (bookingTypePrice ?? 0) > 0;

  const isPrimary = userLocationId === selectedLocationId;

  /* `<`, not `!==`. The server asks the same question as
     `totalFreeBookings() > freeBookingsAvailed`, and the two answers
     have to match: a member who has somehow availed MORE than their
     allowance read as "still has free sessions" here while the server
     called the booking DROP_IN_PAID — which now means the create call
     is refused for arriving without a payment. */
  const hasFree =
    (membershipUsage?.freeBookingsAvailed ?? 0) <
    (membershipUsage?.totalFreeBookingCount ?? 0);

  const isFreeDropIn = (bookingTypePrice ?? 0) === 0;

  return !isPrimary && !hasFree && !isFreeDropIn;
};

/* ------------------------------------------------------------
   2 · PAYLOAD
   ------------------------------------------------------------ */

export type BuildPayloadInput = {
  selectedLocation: Pick<LocationData, 'id' | 'timezone'> | null;
  facilityType: FacilityType;
  selectedDateObj: Date;
  bookingData: Pick<BookingData, 'times'>;
  /** Manually chosen bay. A calendarId on the hitting-bay path,
   *  a room MongoId elsewhere — hence the branch below. */
  selectedBay: string | null;
  /** Booking types that assign a bay themselves. */
  hidesBaySelection: boolean;
  selectedCoachId?: string;
  impliedBayTeamMemberId?: string;
  /** `bookingTypeCalendar.calendarId` from the availability call. */
  availabilityCalendarId?: string;
  hittingBayManualSlots: HittingBaySlot[];
  hittingBayUnionSlots: HittingBaySlot[];
  rescheduleBookingId?: string;
};

/** Timezone used when the location has none. Matches v1. */
const FALLBACK_TZ = 'America/Denver';

/**
 * Assemble the create-booking request.
 *
 * The hitting-bay path differs from every other facility in three
 * ways, all of them load-bearing:
 *   · `calendarId` comes from the chosen slot, not from the
 *     booking-type calendar
 *   · `teamMemberId` is omitted entirely — the calendarId carries
 *     the assignment
 *   · `roomId` is omitted, because `selectedBay` holds a
 *     calendarId there rather than a room id
 */
export const buildBookingPayload = (
  {
    selectedLocation,
    facilityType,
    selectedDateObj,
    bookingData,
    selectedBay,
    hidesBaySelection,
    selectedCoachId,
    impliedBayTeamMemberId,
    availabilityCalendarId,
    hittingBayManualSlots,
    hittingBayUnionSlots,
    rescheduleBookingId,
  }: BuildPayloadInput,
  extra?: object,
) => {
  const isHittingBay = facilityType === FacilityType.HITTING_BAY;

  const selectedStartTime = bookingData.times?.[0]?.split(' - ')[0] || '';

  const activeHittingBaySlot: HittingBaySlot | undefined = isHittingBay
    ? selectedBay
      ? hittingBayManualSlots[0]
      : hittingBayUnionSlots.find((s) => s.startTime === selectedStartTime)
    : undefined;

  return {
    locationId: selectedLocation?.id || '',
    facility: facilityType,
    bookingDate: formatDateForAPIWithTimezone(
      selectedDateObj,
      selectedLocation?.timezone || FALLBACK_TZ,
    ),
    calendarId: isHittingBay
      ? activeHittingBaySlot?.calendarId || ''
      : availabilityCalendarId || '',
    startTime: selectedStartTime,
    endTime: bookingData.times?.at(-1)?.split(' - ')[1] || '',
    timezone: selectedLocation?.timezone,
    ...(isHittingBay
      ? {}
      : selectedCoachId
        ? { teamMemberId: selectedCoachId }
        : impliedBayTeamMemberId
          ? { teamMemberId: impliedBayTeamMemberId }
          : {}),
    ...(!isHittingBay && selectedBay && !hidesBaySelection
      ? { roomId: selectedBay }
      : {}),
    ...(activeHittingBaySlot?.alternateCalendarIds?.length
      ? { alternateCalendarIds: activeHittingBaySlot.alternateCalendarIds }
      : {}),
    ...(rescheduleBookingId ? { isReschedule: true, rescheduleBookingId } : {}),
    ...extra,
  };
};

/* ------------------------------------------------------------
   3 · SLOT LIMIT — how much a member may book in one go
   ------------------------------------------------------------ */

/**
 * Consecutive slots allowed when the booking type sets no daily
 * cap. The designs describe this as "max 2 hours", but that is
 * the fallback, not the rule — a configured `max_mins_per_day`
 * always wins, and it varies by booking type.
 */
export const DEFAULT_MAX_SLOTS = 2;

/** Slot length assumed when the booking type configures none. */
export const DEFAULT_INCREMENT_MINS = 30;

/* ------------------------------------------------------------
   BOOKED WITH A PERSON, NOT A ROOM
   ------------------------------------------------------------ */

/**
 * Facilities where the COACH is the thing being chosen and the bay
 * is not put to the member at all.
 *
 * These book against a coach's calendar and the server assigns the
 * room off it, so the booking payload carries no `roomId` and the
 * bay picker never renders. Their booking-type calendar still lists
 * rooms — which is the trap: without this predicate a club with a
 * single coach falls past the coach step (it only draws for two or
 * more) into a room picker meant for recovery rooms and workspaces,
 * and the member is asked to pick a bay that the server is going to
 * overrule anyway.
 *
 * SHARED BECAUSE IT WAS THREE COPIES. v1 spelled this
 * `alwaysRequiresCoach`, v2 `coachIsTheChoice`, and v2 kept a second
 * near-copy for whether the coach row shows an availability count.
 * Adding COACHING_LESSON meant editing all three and getting two
 * right would have shipped a half-working facility, so the list
 * lives here once and every caller reads it.
 */
const COACH_LED_FACILITIES: FacilityType[] = [
  FacilityType.CLUB_FITTING,
  FacilityType.BOX_ASSESSMENT,
  /* Coaching Lesson is a lesson with a named coach — the same shape
     as Club Fitting, and the reason this list exists rather than two
     inline `||` chains. */
  FacilityType.COACHING_LESSON,
];

export const isCoachLedFacility = (facility?: FacilityType): boolean =>
  !!facility && COACH_LED_FACILITIES.includes(facility);

/**
 * Facilities whose session is booked with a named person, so a booking's
 * assigned user is genuinely the coach the member is meeting.
 *
 * A DIFFERENT QUESTION from `isCoachLedFacility` above, which asks whether the
 * booking flow skips the room picker. Every booking carries an `assignedUser`
 * — it is the GHL appointment's assigned user, i.e. the calendar's staff owner
 * — so anywhere that renders one as "Coach" has to ask this instead, or a solo
 * Hitting Bay names a staff member the member never met.
 *
 * FITNESS_LESSON is here but not in the list above: it books like a room, yet
 * it has an instructor worth naming.
 */
const FACILITIES_WITH_A_NAMED_COACH: FacilityType[] = [
  FacilityType.CLUB_FITTING,
  FacilityType.BOX_ASSESSMENT,
  FacilityType.COACHING_LESSON,
  FacilityType.FITNESS_LESSON,
];

/**
 * The coach's name for a booking, or null when there is nobody to name.
 *
 * Filtered rather than interpolated: `${firstName} ${lastName}` renders the
 * literal "undefined" for a missing part, and an assignedUser carrying blank
 * names produced a truthy string that displayed as nothing — which is how a
 * "Coach" label came to sit above an empty line.
 */
export const coachNameFor = (booking: {
  facility?: string;
  assignedUser?: { firstName?: string; lastName?: string };
}): string | null => {
  if (!FACILITIES_WITH_A_NAMED_COACH.includes(booking.facility as FacilityType)) {
    return null;
  }

  return (
    [booking.assignedUser?.firstName, booking.assignedUser?.lastName]
      .filter((part) => typeof part === 'string' && part.trim() !== '')
      .join(' ')
      .trim() || null
  );
};

/* ------------------------------------------------------------
   ENTITLEMENT-USAGE COPY — shared between v1 and v2 checkout
   ------------------------------------------------------------ */

/**
 * `entitlementSessionsUsed` / `entitlementUsageLabel` — the single
 * shared derivation and the single shared sentence both UI trees
 * render at entitlement checkout (BookingCheckout.tsx and
 * v2/screens/SelectSlots.tsx).
 *
 * Why the noun stays plural at n=1. "of your remaining sessions" is
 * partitive — it names the pool being drawn from, not the quantity
 * drawn. "Uses 1 of your remaining sessions" is correct English
 * exactly as shipped, so there is no singular branch to write and
 * none should be added. Only the numeral varies.
 *
 * Why the floor is 1. It mirrors `Math.max(1, ...)` in the server's
 * `computeEntitlementSlotCount` (340-10). A booking that reached this
 * sentence definitively consumes the facility, so 0 is never the
 * honest answer, and "Uses 0 of your remaining sessions" would read
 * as free.
 *
 * Why the count is client-derived and that is not a trust hole. The
 * availability grid is generated from the calendar's `slotDuration`,
 * so N consecutive slots span N × slotDuration minutes, which is
 * exactly what the server independently rounds to N. This value is
 * display only; the ledger debit is derived server-side from the
 * persisted booking bounds and never reads a client-supplied count
 * (340-10, T-340-10-01).
 */
export const entitlementSessionsUsed = (slotCount?: number): number =>
  Math.max(1, Number.isFinite(slotCount) ? Math.floor(slotCount as number) : 1);

export const entitlementUsageLabel = (slotCount?: number): string =>
  `Uses ${entitlementSessionsUsed(slotCount)} of your remaining sessions`;

export type SlotLimitInput = {
  /** `bookingType.fields` from the API. */
  fields?: Record<string, { value?: any; enabled: boolean }>;
  /** Minutes this member has already booked today for this facility. */
  todayMinutesBooked?: number;
  /**
   * Measured from a real availability slot (end − start). Pass it
   * when known: it guards against a booking type configured with a
   * 30-minute increment while the calendar actually serves 60.
   */
  slotDurationMins?: number;
};

export type SlotLimit = {
  bookingIncrement: number;
  /**
   * Minutes one slot actually spends — the measured slot length when
   * the calendar supplied one, else the configured increment.
   *
   * Exposed because `maxSelectableSlots` is derived from THIS and not
   * from `bookingIncrement`, and the two differ whenever a booking
   * type is configured at one length while its calendar serves
   * another. Anything turning a slot count back into minutes must
   * multiply by this, or it will quote a budget the member does not
   * have.
   */
  perSlotMins: number;
  /** null when the booking type sets no daily cap. */
  maxMinsPerDay: number | null;
  /** Infinity when uncapped. */
  remainingMins: number;
  /** Consecutive slots the member may select right now. */
  maxSelectableSlots: number;
  isDailyLimitReached: boolean;
};

/**
 * Resolve how many consecutive slots a member may take.
 *
 * Capped types spend against a daily minute budget, so the answer
 * shrinks as the day is used up and reaches zero once it is spent.
 * Uncapped types fall back to `DEFAULT_MAX_SLOTS`.
 */
export const deriveSlotLimit = ({
  fields,
  todayMinutesBooked = 0,
  slotDurationMins,
}: SlotLimitInput): SlotLimit => {
  const bookingIncrement = fields?.booking_increments?.enabled
    ? (fields.booking_increments.value ?? DEFAULT_INCREMENT_MINS)
    : DEFAULT_INCREMENT_MINS;

  const maxMinsPerDay = fields?.max_mins_per_day?.enabled
    ? (fields.max_mins_per_day.value ?? null)
    : null;

  const remainingMins =
    maxMinsPerDay != null
      ? Math.max(0, maxMinsPerDay - todayMinutesBooked)
      : Infinity;

  /* Prefer the measured slot length; fall back to the configured
     increment, and never divide by zero. */
  const perSlot =
    slotDurationMins && slotDurationMins > 0 ? slotDurationMins : bookingIncrement;

  const maxSelectableSlots =
    maxMinsPerDay != null
      ? Math.max(0, Math.floor(remainingMins / perSlot))
      : DEFAULT_MAX_SLOTS;

  return {
    bookingIncrement,
    perSlotMins: perSlot,
    maxMinsPerDay,
    remainingMins,
    maxSelectableSlots,
    isDailyLimitReached: maxMinsPerDay != null && remainingMins <= 0,
  };
};

/* ------------------------------------------------------------
   4 · WAIVER GATE
   ------------------------------------------------------------ */

/**
 * The waiver a member must sign before booking at this club, or
 * null if there is nothing to sign.
 *
 * Swallows failures and returns null — matching v1, where a
 * waiver-service outage lets the booking through rather than
 * blocking every member at every club. That is a deliberate
 * availability-over-strictness trade, and it is recorded here so
 * it is a decision rather than an accident.
 */
export const getWaiverGate = async (
  locationId: string,
): Promise<WaiverData | null> => {
  try {
    const status = await waiverService.getWaiverStatus(locationId);
    if (status.waiver && !status.hasSigned) return status.waiver;
  } catch {
    /* Intentional: see above. */
  }
  return null;
};

/* ------------------------------------------------------------
   5 · HOW FAR AHEAD A MEMBER MAY BOOK
   ------------------------------------------------------------ */

/**
 * Last bookable day, from the booking type's Box OS config — null when
 * unlimited.
 *
 * ⚠️ `advance_booking_days` HOLDS HOURS, NOT DAYS. The API's enum is
 * `ADVANCE_BOOKING_HOURS = 'advance_booking_days'` — the key was named days
 * and the value was always hours — and `canBookFacility` compares it against
 * `differenceInHours`, then divides by 24 for the member-facing message. A
 * stored 168 is 7 days, not 168. Reading it as days is how the picker came to
 * offer half a year.
 *
 * No `enabled` check, deliberately: the API applies the value regardless, so
 * honouring the flag here would let the picker offer dates the server then
 * refuses.
 */
export const deriveMaxBookableDate = (
  fields: SlotLimitInput['fields'],
  from: Date = new Date(),
): Date | null => {
  const hours = fields?.advance_booking_days?.value;
  if (typeof hours !== 'number' || hours <= 0) return null;

  const max = new Date(from);
  max.setHours(0, 0, 0, 0);
  max.setDate(max.getDate() + Math.floor(hours / 24));
  return max;
};
